import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { AuthLayout, DashboardLayout } from '@layouts/index'
import { Loader } from '@components/index'
import { ProtectedRoute } from './ProtectedRoute'
import { PublicRoute } from './PublicRoute'
import { ROUTES } from './paths'

const Login = lazy(() => import('@pages/Login').then((m) => ({ default: m.Login })))
const ForgotPassword = lazy(() =>
  import('@pages/Login').then((m) => ({ default: m.ForgotPassword })),
)
const ResetPassword = lazy(() =>
  import('@pages/Login').then((m) => ({ default: m.ResetPassword })),
)
const Dashboard = lazy(() => import('@pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const UsersList = lazy(() => import('@pages/Users').then((m) => ({ default: m.UsersList })))
const UserDetail = lazy(() => import('@pages/Users').then((m) => ({ default: m.UserDetail })))
const BookingsList = lazy(() =>
  import('@pages/Bookings').then((m) => ({ default: m.BookingsList })),
)
const BookingDetail = lazy(() =>
  import('@pages/Bookings').then((m) => ({ default: m.BookingDetail })),
)
const FeedbackList = lazy(() =>
  import('@pages/Feedback').then((m) => ({ default: m.FeedbackList })),
)
const FeedbackDetail = lazy(() =>
  import('@pages/Feedback').then((m) => ({ default: m.FeedbackDetail })),
)
const NotificationsTemplates = lazy(() =>
  import('@pages/Notifications').then((m) => ({ default: m.NotificationsTemplates })),
)
const MonetizationSettings = lazy(() =>
  import('@pages/Monetization').then((m) => ({ default: m.MonetizationSettings })),
)
const AiUsage = lazy(() => import('@pages/AiUsage').then((m) => ({ default: m.AiUsage })))
const LookupsList = lazy(() =>
  import('@pages/Lookups').then((m) => ({ default: m.LookupsList })),
)
const LookupCategoryDetail = lazy(() =>
  import('@pages/Lookups').then((m) => ({ default: m.LookupCategoryDetail })),
)
const Profile = lazy(() => import('@pages/Profile').then((m) => ({ default: m.Profile })))
const Settings = lazy(() => import('@pages/Settings').then((m) => ({ default: m.Settings })))
const NotFound = lazy(() => import('@pages/NotFound').then((m) => ({ default: m.NotFound })))

export const AppRoutes = () => {
  const location = useLocation()
  return (
    <Suspense fallback={<Loader fullScreen />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname.split('/')[1] || 'root'}>
          <Route
            element={
              <PublicRoute>
                <AuthLayout />
              </PublicRoute>
            }
          >
            <Route path={ROUTES.login} element={<Login />} />
            <Route path={ROUTES.forgotPassword} element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
          </Route>

          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to={ROUTES.dashboard} replace />} />
            <Route path={ROUTES.dashboard} element={<Dashboard />} />

            {/* Users — admin only */}
            <Route
              path={ROUTES.users}
              element={
                <ProtectedRoute roles={['admin']}>
                  <UsersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id"
              element={
                <ProtectedRoute roles={['admin']}>
                  <UserDetail />
                </ProtectedRoute>
              }
            />

            {/* Bookings */}
            <Route path={ROUTES.bookings} element={<BookingsList />} />
            <Route path="/bookings/:id" element={<BookingDetail />} />

            {/* Feedback */}
            <Route path={ROUTES.feedback} element={<FeedbackList />} />
            <Route path="/feedback/:id" element={<FeedbackDetail />} />

            {/* Notifications — admin only */}
            <Route
              path={ROUTES.notifications}
              element={
                <ProtectedRoute roles={['admin']}>
                  <NotificationsTemplates />
                </ProtectedRoute>
              }
            />

            {/* Monetization — admin only */}
            <Route
              path={ROUTES.monetization}
              element={
                <ProtectedRoute roles={['admin']}>
                  <MonetizationSettings />
                </ProtectedRoute>
              }
            />

            {/* AI Usage — admin only */}
            <Route
              path={ROUTES.aiUsage}
              element={
                <ProtectedRoute roles={['admin']}>
                  <AiUsage />
                </ProtectedRoute>
              }
            />

            {/* Lookups — admin only */}
            <Route
              path={ROUTES.lookups}
              element={
                <ProtectedRoute roles={['admin']}>
                  <LookupsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lookups/:id"
              element={
                <ProtectedRoute roles={['admin']}>
                  <LookupCategoryDetail />
                </ProtectedRoute>
              }
            />

            {/* Profile & Settings */}
            <Route path={ROUTES.profile} element={<Profile />} />
            <Route path={ROUTES.settings} element={<Settings />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  )
}
